import { type ReactNode } from 'react'
import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { twMerge } from 'tailwind-merge'
import { X } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import type { PartProps } from '../../../lib/part-props'

const sheetVariants = cva('fixed bg-background-overlay shadow-lg transition-all duration-300 outline-none', {
  variants: {
    side: {
      right:
        'inset-y-0 right-0 h-full w-[600px] border-l data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full',
      left: 'inset-y-0 left-0 h-full w-[600px] border-r data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full',
      top: 'inset-x-0 top-0 h-[300px] border-b data-[starting-style]:-translate-y-full data-[ending-style]:-translate-y-full',
      bottom:
        'inset-x-0 bottom-0 h-[300px] border-t data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full',
    },
  },
  defaultVariants: {
    side: 'right',
  },
})

interface SheetContentProps extends PartProps<BaseDialog.Popup.Props>, VariantProps<typeof sheetVariants> {
  className?: string
  backdropClassName?: string
  children: ReactNode
  showClose?: boolean
}

export function SheetContent({
  className,
  backdropClassName,
  children,
  showClose = true,
  side = 'right',
}: SheetContentProps) {
  const closeButtonPositionClasses = {
    right: 'right-4 top-4',
    left: 'left-4 top-4',
    top: 'right-4 top-4',
    bottom: 'right-4 bottom-4',
  }

  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop
        className={twMerge(
          'bg-backdrop fixed inset-0 transition-all duration-300 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
          backdropClassName,
        )}
      />
      <BaseDialog.Popup className={twMerge(sheetVariants({ side }), 'flex flex-col', className)}>
        {showClose && (
          <BaseDialog.Close
            className={twMerge(
              'text-foreground-secondary hover:text-foreground bg-element hover:bg-element-hover absolute flex size-6 items-center justify-center rounded-full transition-colors',
              'outline-none',
              closeButtonPositionClasses[side as keyof typeof closeButtonPositionClasses],
            )}
            aria-label="Close sheet"
          >
            <X className="size-3 stroke-[length:var(--icon-stroke-width-bold)]" />
          </BaseDialog.Close>
        )}
        {children}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  )
}
